import { apiClient } from "../services/apiClient";
import { ATP_API_URL } from "../config/apiConfig";
// import { getUserData } from "../../utils/authnicate";
import { getUserData } from "utils";
import { normalizeLedgerNameList } from "../utils/ledgerUtils";

// ── Normalizers ────────────────────────────────────────────────────────────────
const normalizeCategory = (v) => {
  if (!v) return "";
  const m = { client: "client", "service partner": "Service Partner" };
  return m[v.toLowerCase().trim()] ?? v;
};

const normalizeLOB = (v) => {
  if (!v) return "";
  const m = {
    travel: "travel",
    health: "health",
    auto: "auto",
    gadget: "gadget",
    company: "company",
    corporate: "corporate",
    "non-travel": "company",
    // service variants — keep as-is for dept map lookup
    travel_service: "travel_service",
    auto_service: "auto_service",
    gadget_service: "gadget_service",
    mobile_service: "mobile_service",
  };
  return m[v.toLowerCase().trim()] ?? v.toLowerCase();
};

const normalizeEntityType = (v) => {
  if (!v) return "";
  const m = {
    proprietor: "proprietorship",
    proprietorship: "proprietorship",
    partnership: "partnership",
    llp: "llp",
    "private limited": "private_limited",
    private_limited: "private_limited",
    "pvt ltd": "private_limited",
    "pvt. ltd": "private_limited",
    "public limited": "public_limited",
    public_limited: "public_limited",
    trust: "trust",
    society: "society",
  };
  return m[v.toLowerCase().trim()] ?? v;
};

const normalizeAccountType = (v) => {
  if (!v) return "";
  const m = { saving: "savings", savings: "savings", current: "current" };
  return m[v.toLowerCase().trim()] ?? v.toLowerCase();
};

const normalizeDept = (v) => {
  if (!v) return "";
  // Capitalize first letter of each word to match dropdown option values
  return v.replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── Map nested API record → { entity, documents } ─────────────────────────────
const mapRecord = (r) => {
  // ── Convenience accessors for nested sub-objects ──────────────────────────
  const contact = r.contactPersonDetails || {};
  const bank = r.bankDetails || {};
  const msme = r.msmeDetails || {};
  const utility = r.utilityBillDetails || {};
  const pan = r.panDetails || {};
  const aadhar = r.adharDetails || {};
  const prop = r.proprietorshipDetails || {};
  const partner = r.partnershipDetails || {};
  const llp = r.llpDetails || {};
  const pvt = r.privatePublicLimitedCompanyDetails || {};
  const trust = r.trustDetails || {};

  // ── Build documents array from nested path fields ─────────────────────────
  const docPathMap = [
    {
      key: "doc_aadhar",
      label: "Aadhar Card",
      path:
        prop.aadharCardPath ||
        aadhar.aadharCardPath ||
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        r.aadharCardPath,
      isApproved:
        prop.isAadharCard === true ||
        aadhar.isAadharCard === true ||
        partner.isAadharOfBothPartners === true ||
        llp.isAadharOfBothPartners === true,
      isDeficient:
        prop.isAadharCardDef === true ||
        aadhar.isAadharCardDef === true ||
        partner.isAadharOfBothPartnersDef === true ||
        llp.isAadharOfBothPartnersDef === true,
    },
    {
      key: "aadharCardBackPath",
      label: "Aadhar Card (Back)",
      path:
        r.aadharCardBackPath ||
        prop.aadharCardBackPath ||
        aadhar.aadharCardBackPath ||
        llp.aadharCardBackPath ||
        partner.aadharCardBackPath ||
        "",
      isApproved: false,
      isDeficient: false,
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      path: prop.panCardPath || pan.panCardPath,
      isApproved: prop.isPANCard === true || pan.isPANCard === true,
      isDeficient: prop.isPANCardDef === true,
    },
    {
      key: "doc_partnershipDeed",
      label: "Partnership Deed",
      path: partner.partnershipDeedPath || llp.partnershipDeedPath,
      isApproved:
        partner.isPartnershipDeed === true || llp.isPartnershipDeed === true,
      isDeficient:
        partner.isPartnershipDeedDef === true ||
        llp.isPartnershipDeedDef === true,
    },
    {
      key: "doc_aadharBoth",
      label: "Aadhar of Both Partners",
      path:
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        prop.aadharCardPath ||
        aadhar.aadharCardPath ||
        r.aadharCardPath,
      isApproved:
        partner.isAadharOfBothPartners === true ||
        llp.isAadharOfBothPartners === true ||
        prop.isAadharCard === true ||
        aadhar.isAadharCard === true,
      isDeficient:
        partner.isAadharOfBothPartnersDef === true ||
        llp.isAadharOfBothPartnersDef === true ||
        prop.isAadharCardDef === true ||
        aadhar.isAadharCardDef === true,
    },
    {
      key: "doc_aadharBothSecondary",
      label: "Aadhar of Secondary Partner",
      path:
        partner.aadharOfBothPartnersPathSecond ||
        llp.aadharOfBothPartnersPathSecond,
      isApproved:
        partner.isAadharOfBothPartnersSecond === true ||
        llp.isAadharOfBothPartnersSecond === true,
      isDeficient:
        partner.isAadharOfBothPartnersDefSecond === true ||
        llp.isAadharOfBothPartnersDefSecond === true,
    },
    {
      key: "doc_llpCertificate",
      label: "LLP Certificate",
      path: llp.uploadLLPCertificatePath,
      isApproved: llp.isLLPCertificate === true,
      isDeficient: llp.isLLPCertificateDef === true,
    },
    {
      key: "doc_coi",
      label: "Certificate of Incorporation",
      path: pvt.certificateOfIncorporationPath,
      isApproved: pvt.isCertificateOfIncorporation === true,
      isDeficient: pvt.isCertificateOfIncorporationDef === true,
    },
    {
      key: "doc_directorPan",
      label: "Director PAN",
      path: pvt.directorPANPath,
      isApproved: pvt.isDirectorPan === true,
      isDeficient: pvt.isDirectorPanDef === true,
    },
    {
      key: "doc_directorAadhar",
      label: "Director Aadhar",
      path: pvt.directorAadharPath,
      isApproved: pvt.isDirectorAadhar === true,
      isDeficient: pvt.isDirectorAadharDef === true,
    },
    {
      key: "doc_trustDeed",
      label: "Trust Deed",
      path: trust.trustDeedPath,
      isApproved: trust.isTrustDeed === true,
      isDeficient: trust.isTrustDeedDef === true,
    },
    {
      key: "doc_panTrust",
      label: "PAN of Trust",
      path: trust.trustPanPath,
      isApproved: trust.isPANOfTrust === true,
      isDeficient: trust.isPANOfTrustDef === true,
    },
    {
      key: "doc_aadharTrustee",
      label: "Aadhar of Trustee",
      path: trust.trusteeAadharPath,
      isApproved: trust.isAadharOfTrustee === true,
      isDeficient: trust.isAadharOfTrusteeDef === true,
    },
    {
      key: "doc_panTrustee",
      label: "PAN of Trustee",
      path: trust.trusteePanPath,
      isApproved: trust.isPANOfTrustee === true,
      isDeficient: trust.isPANOfTrusteeDef === true,
    },
    {
      key: "doc_msme_certificate",
      label: "MSME Certificate",
      path: msme.msmeCertificatePath,
      isApproved: msme.isMSMECertificate === true,
      isDeficient: msme.isMSMECertificateDef === true,
    },
    {
      key: "doc_utility_bill",
      label: "Utility Bill",
      path: utility.utilityBillDocumentPath,
      isApproved: utility.isUtilityBillDocument === true,
      isDeficient: utility.isUtilityBillDocumentDef === true,
    },
    {
      key: "doc_bank_proof",
      label: "Passbook / Cancelled Cheque",
      path: bank.passbookOrCancelledChequePath,
      isApproved: bank.isPassbookOrCancelledCheque === true,
      isDeficient: bank.isPassbookOrCancelledChequeDef === true,
    },
    {
      key: "third_party_file",
      label: "Signed Form (Third Party)",
      path: bank.uploadSignedFormPath,
      isApproved: bank.isSignedForm === true,
      isDeficient: bank.isSignedFormDef === true,
    },
  ];

  const documents = docPathMap
    .filter((d) => d.path && d.path !== "string")
    .map((d) => ({
      key: d.key,
      documentName: d.label,
      isApproved: d.isApproved,
      isDeficient: d.isDeficient,
      files: [
        {
          isUploaded: true,
          fileName: d.path.split(/[\\/]/).pop() || d.label,
          filePath: d.path,
        },
      ],
    }));

  (r.gstList || []).forEach((g, i) => {
    const certPath =
      g.gstCertificatePath && g.gstCertificatePath !== "string"
        ? g.gstCertificatePath
        : null;
    documents.push({
      key: `gst_file_${i}`,
      documentName: `GST Certificate — ${g.gstNumber || i + 1}`,
      isApproved: g.isGST === true,
      isDeficient: g.isGSTDef === true,
      files: certPath
        ? [
            {
              isUploaded: true,
              fileName: certPath.split("\\").pop() || "GST",
              filePath: certPath,
            },
          ]
        : [],
    });
  });

  const mapped = {
    entity: {
      id: r.entityPrimaryId,
      entityId: r.entityId || "",
      entityPrimaryId: r.entityPrimaryId,
      kycId: r.kycId,
      parentId: r.parentId,
      parentCompanyName: r.parentName || "",
      isResendLink: r.isResendLink || false,
      resendLinkCount: r.resendLinkCount || 0,
      parentCode: r.parentCode || "",
      // folderNo — present when entity was uploaded via bulk upload (used to fetch docs)
      folderNo: r.folderNo || null,

      // ── Primary ──────────────────────────────────────────────────────────
      entityName: r.entityName || "",
      EntityName: r.entityName || "",
      entityCategory: normalizeCategory(r.entityCategory),
      entityLOBType: normalizeLOB(r.entityLOBType),
      lobType: normalizeLOB(r.entityLOBType),
      aaDepartmentFunction: normalizeDept(r.aaDepartmentFunction),
      emailAddress: r.emailAddress || "",
      contactPersonEmail: r.emailAddress || "",
      spocEmailAddress: r.spoC_EmailAddress || "",
      requireKYC: r.requireKYC,
      activeLink: r.activeLink,
      kyC_URL: r.kyC_URL || "",
      isApproved: r.isVerified || false,
      isVerified: r.isVerified || false,
      isDeficiency: r.isDeficiency || false,
      createdBy: r.createdBy || "",
      createdOn: r.createdOn || "",
      modifiedOn: r.modifiedOn || "",
      kycCreatedOn: r.kycCreatedOn || "",
      verifiedOn: r.verifiedOn || "",

      // ── KYC entity info ───────────────────────────────────────────────────
      parentName: r.parentName || "",
      tradeName: r.tradeName || "",
      pinCode: r.pinCode || "",
      city: r.city || "",
      State: r.state || "",
      state: r.state || "",
      address: r.address || "",
      natureOfBusiness: r.natureOfBusiness || "",
      invoiceRequired: r.invoiceRequired ?? null,
      isInvoiceRequired:
        r.invoiceRequired === true
          ? "yes"
          : r.invoiceRequired === false
            ? "no"
            : "",
      entityType: normalizeEntityType(r.entityType),

      // ── Contact person ────────────────────────────────────────────────────
      contactPersonName: contact.contactPersonName || "",
      contactPersonDesignation: contact.designation || "",
      contactPersonMobile: contact.mobileNumber || "",
      contactEmail: contact.contactPersonEmail || r.emailAddress || "",

      // ── PAN / Aadhar (proprietorship uses proprietorshipDetails) ──────────
      panCardNumber: prop.panCardNumber || pan.panCardNumber || "",
      aadharCardNumber:
        prop.aadharCardNumber ||
        aadhar.aadharCardNumber ||
        partner.aadharNumberOfBothPartners ||
        llp.aadharNumberOfBothPartners ||
        "",
      CompanypanNumber: prop.panCardNumber || pan.panCardNumber || "",

      // ── Partnership ───────────────────────────────────────────────────────
      partnershipDeedNumber: partner.partnershipDeedPath || "",
      aadharNumberOfBothPartners:
        partner.aadharNumberOfBothPartners ||
        llp.aadharNumberOfBothPartners ||
        prop.aadharCardNumber ||
        aadhar.aadharCardNumber ||
        "",
      aadharNumberOfBothPartnersSecond:
        partner.aadharNumberOfBothPartnersSecond ||
        llp.aadharNumberOfBothPartnersSecond ||
        "",
      aadharOfBothPartnersPathSecond:
        partner.aadharOfBothPartnersPathSecond ||
        llp.aadharOfBothPartnersPathSecond ||
        "",
      aadharOfBothPartnersUploadedSecond:
        partner.aadharOfBothPartnersUploadedSecond === true ||
        llp.aadharOfBothPartnersUploadedSecond === true,
      isAadharOfBothPartnersSecond:
        partner.isAadharOfBothPartnersSecond === true ||
        llp.isAadharOfBothPartnersSecond === true,
      isAadharOfBothPartnersDefSecond:
        partner.isAadharOfBothPartnersDefSecond === true ||
        llp.isAadharOfBothPartnersDefSecond === true,

      // ── LLP ───────────────────────────────────────────────────────────────
      llpCrtificateNumber: llp.llpCrtificateNumber || "",

      // ── Private / Public Limited ──────────────────────────────────────────
      certificateOfIncorporationNumber:
        pvt.certificateOfIncorporationNumber || "",
      directorPANNumber: pvt.directorPANNumber || "",
      directorAadharNumber: pvt.directorAadharNumber || "",

      // ── Trust ─────────────────────────────────────────────────────────────
      trustDeedNumber: trust.trustDeedNumber || "",
      panOfTrustNumber: trust.trustPanNumber || "",
      aadharNumberOfTrustee: trust.trusteeAadharNumber || "",
      panOfTrusteeNumber: trust.trusteePanNumber || "",

      // ── MSME ──────────────────────────────────────────────────────────────
      isRegisteredUnderMSME: msme.isRegisteredUnderMSME,
      isMSMERegistered:
        msme.isRegisteredUnderMSME === true
          ? "yes"
          : msme.isRegisteredUnderMSME === false
            ? "no"
            : "",
      msmeNumber: msme.msmeRegistrationNumber || "",
      msmeRegistrationNumber: msme.msmeRegistrationNumber || "",
      msmeStatus: msme.msmeStatus || "", // TODO: adding the msme status

      // ── GST ───────────────────────────────────────────────────────────────
      isGSTRegistered:
        r.isGSTRegistered === true
          ? "yes"
          : r.isGSTRegistered === false
            ? "no"
            : "",
      gstList: r.gstList || [],

      // ── Utility bill ──────────────────────────────────────────────────────
      utilityType: utility.utilityType || "",
      utilityProviderName: utility.providerName || "",
      utilityAccountNumber: utility.accountOrConsumerNumber || "",
      utilityAddress: utility.addressOnUtilityBill || "",
      providerName: utility.providerName || "",
      accountOrConsumerNumber: utility.accountOrConsumerNumber || "",
      addressOnUtilityBill: utility.addressOnUtilityBill || "",

      // ── Bank ──────────────────────────────────────────────────────────────
      hasBankDetails:
        bank.provideBankDetails === true
          ? "yes"
          : bank.provideBankDetails === false
            ? "no"
            : "",
      bankName: bank.bankName || "",
      accountNumber: bank.accountNumber || "",
      ifscCode: bank.ifscCode || "",
      accountType: normalizeAccountType(bank.accountType),
      branchName: bank.branchName || "",
      beneficiaryName: bank.beneficiaryName || "",
      relationWithBeneficiary: bank.relationWithBeneficiary || "",

      // ── Document paths (kept for backward compat with EditModal) ──────────
      aadharCardPath:
        prop.aadharCardPath ||
        aadhar.aadharCardPath ||
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        r.aadharCardPath ||
        "",
      aadharCardBackPath:
        r.aadharCardBackPath ||
        prop.aadharCardBackPath ||
        aadhar.aadharCardBackPath ||
        llp.aadharCardBackPath ||
        partner.aadharCardBackPath ||
        "",
      panCardPath: prop.panCardPath || pan.panCardPath || "",
      partnershipDeedPath:
        partner.partnershipDeedPath || llp.partnershipDeedPath || "",
      uploadLLPCertificatePath: llp.uploadLLPCertificatePath || "",
      aadharOfBothPartnersPath:
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        prop.aadharCardPath ||
        aadhar.aadharCardPath ||
        r.aadharCardPath ||
        "",
      certificateOfIncorporationPath: pvt.certificateOfIncorporationPath || "",
      directorPANPath: pvt.directorPANPath || "",
      directorAadharPath: pvt.directorAadharPath || "",
      msmeCertificatePath: msme.msmeCertificatePath || "",
      utilityBillDocumentPath: utility.utilityBillDocumentPath || "",
      passbookOrCancelledChequePath: bank.passbookOrCancelledChequePath || "",
      uploadSignedFormPath: bank.uploadSignedFormPath || "",
      trustDeedPath: trust.trustDeedPath || "",
      panOfTrustPath: trust.trustPanPath || "",
      aadharOfTrusteePath: trust.trusteeAadharPath || "",
      panOfTrusteePath: trust.trusteePanPath || "",

      // ── Document approval flags (from API — preserved for update payload) ─
      isAadharCard:
        prop.isAadharCard === true ||
        aadhar.isAadharCard === true ||
        partner.isAadharOfBothPartners === true ||
        llp.isAadharOfBothPartners === true,
      isAadharCardDef:
        prop.isAadharCardDef === true ||
        aadhar.isAadharCardDef === true ||
        partner.isAadharOfBothPartnersDef === true ||
        llp.isAadharOfBothPartnersDef === true,
      isPANCard: prop.isPANCard === true || pan.isPANCard === true,
      isPANCardDef: prop.isPANCardDef === true || pan.isPANCardDef === true,
      isPartnershipDeed:
        partner.isPartnershipDeed === true || llp.isPartnershipDeed === true,
      isPartnershipDeedDef:
        partner.isPartnershipDeedDef === true ||
        llp.isPartnershipDeedDef === true,
      isAadharOfBothPartners:
        partner.isAadharOfBothPartners === true ||
        llp.isAadharOfBothPartners === true ||
        prop.isAadharCard === true ||
        aadhar.isAadharCard === true,
      isAadharOfBothPartnersDef:
        partner.isAadharOfBothPartnersDef === true ||
        llp.isAadharOfBothPartnersDef === true ||
        prop.isAadharCardDef === true ||
        aadhar.isAadharCardDef === true,
      isLLPCertificate: llp.isLLPCertificate === true,
      isLLPCertificateDef: llp.isLLPCertificateDef === true,
      isCertificateOfIncorporation: pvt.isCertificateOfIncorporation === true,
      isCertificateOfIncorporationDef:
        pvt.isCertificateOfIncorporationDef === true,
      isDirectorPan: pvt.isDirectorPan === true,
      isDirectorPanDef: pvt.isDirectorPanDef === true,
      isDirectorAadhar: pvt.isDirectorAadhar === true,
      isDirectorAadharDef: pvt.isDirectorAadharDef === true,
      isTrustDeed: trust.isTrustDeed === true,
      isTrustDeedDef: trust.isTrustDeedDef === true,
      isPANOfTrust: trust.isPANOfTrust === true,
      isPANOfTrustDef: trust.isPANOfTrustDef === true,
      isPANOfTrustee: trust.isPANOfTrustee === true,
      isPANOfTrusteeDef: trust.isPANOfTrusteeDef === true,
      isAadharOfTrustee: trust.isAadharOfTrustee === true,
      isAadharOfTrusteeDef: trust.isAadharOfTrusteeDef === true,
      isMSMECertificate: msme.isMSMECertificate === true,
      isMSMECertificateDef: msme.isMSMECertificateDef === true,
      isUtilityBillDocument: utility.isUtilityBillDocument === true,
      isUtilityBillDocumentDef: utility.isUtilityBillDocumentDef === true,
      isPassbookOrCancelledCheque: bank.isPassbookOrCancelledCheque === true,
      isPassbookOrCancelledChequeDef:
        bank.isPassbookOrCancelledChequeDef === true,
      isSignedForm: bank.isSignedForm === true,
      isSignedFormDef: bank.isSignedFormDef === true,

      // ── Document uploaded flags (from API — preserved for update payload) ─
      aadharCardUploaded:
        prop.aadharCardUploaded === true || aadhar.aadharCardUploaded === true,
      panCardUploaded:
        prop.panCardUploaded === true || pan.panCardUploaded === true,
      partnershipDeedUploaded:
        partner.partnershipDeedUploaded === true ||
        llp.partnershipDeedUploaded === true,
      aadharOfBothPartnersUploaded:
        partner.aadharOfBothPartnersUploaded === true ||
        llp.aadharOfBothPartnersUploaded === true,
      llpCertificateUploaded: llp.llpCertificateUploaded === true,
      certificateOfIncorporationUploaded:
        pvt.certificateOfIncorporationUploaded === true,
      directorPanUploaded: pvt.directorPanUploaded === true,
      directorAadharUploaded: pvt.directorAadharUploaded === true,
      trustDeedUploaded: trust.trustDeedUploaded === true,
      trustPanUploaded: trust.trustPanUploaded === true,
      trusteePanUploaded: trust.trusteePanUploaded === true,
      trusteeAadharUploaded: trust.trusteeAadharUploaded === true,
      msmeCertificateUploaded: msme.msmeCertificateUploaded === true,
      utilityBillDocumentUploaded: utility.utilityBillDocumentUploaded === true,
      passbookOrCancelledChequeUploaded:
        bank.passbookOrCancelledChequeUploaded === true,
      signedFormUploaded: bank.signedFormUploaded === true,

      isDocumentUploaded: r.isDocumentUploaded,
      isSaveAsDraft: r.isSaveAsDraft,

      // ── Ledger files ──────────────────────────────────────────────────────
      ledgerList: r.ledgerList || [],
      ledgerNameList: normalizeLedgerNameList(r.ledgerNameList),
      isLedger: r.isLedger, // "yes" = required, "no" = not required
      isLedgerUploaded: r.isLedgerUploaded || false,
      kycCreatedBy: r.kycCreatedBy,

      // ── Deficiency flags — keyed by doc key, value = { flagName, filePath } ──
      // Parsed from all *Def booleans in the nested sub-objects.
      // Used by EditClientModal to know which docs are deficient.
      deficiencyMap: (() => {
        const map = {};
        const DEF_ENTRIES = [
          {
            key: "doc_aadhar",
            flagName: "isAadharCardDef",
            isDef: prop.isAadharCardDef,
            filePath: prop.aadharCardPath || "",
          },
          {
            key: "doc_pan",
            flagName: "isPANCardDef",
            isDef: prop.isPANCardDef,
            filePath: prop.panCardPath || "",
          },
          {
            key: "doc_partnershipDeed",
            flagName: "isPartnershipDeedDef",
            isDef: partner.isPartnershipDeedDef || llp.isPartnershipDeedDef,
            filePath:
              partner.partnershipDeedPath || llp.partnershipDeedPath || "",
          },
          {
            key: "doc_aadharBoth",
            flagName: "isAadharOfBothPartnersDef",
            isDef:
              partner.isAadharOfBothPartnersDef ||
              llp.isAadharOfBothPartnersDef,
            filePath:
              partner.aadharOfBothPartnersPath ||
              llp.aadharOfBothPartnersPath ||
              "",
          },
          {
            key: "doc_aadharBothSecondary",
            flagName: "isAadharOfBothPartnersDefSecond",
            isDef:
              partner.isAadharOfBothPartnersDefSecond ||
              llp.isAadharOfBothPartnersDefSecond,
            filePath:
              partner.aadharOfBothPartnersPathSecond ||
              llp.aadharOfBothPartnersPathSecond ||
              "",
          },
          {
            key: "doc_llpCertificate",
            flagName: "isLLPCertificateDef",
            isDef: llp.isLLPCertificateDef,
            filePath: llp.uploadLLPCertificatePath || "",
          },
          {
            key: "doc_coi",
            flagName: "isCertificateOfIncorporationDef",
            isDef: pvt.isCertificateOfIncorporationDef,
            filePath: pvt.certificateOfIncorporationPath || "",
          },
          {
            key: "doc_directorPan",
            flagName: "isDirectorPanDef",
            isDef: pvt.isDirectorPanDef,
            filePath: pvt.directorPANPath || "",
          },
          {
            key: "doc_directorAadhar",
            flagName: "isDirectorAadharDef",
            isDef: pvt.isDirectorAadharDef,
            filePath: pvt.directorAadharPath || "",
          },
          {
            key: "doc_trustDeed",
            flagName: "isTrustDeedDef",
            isDef: trust.isTrustDeedDef,
            filePath: trust.trustDeedPath || "",
          },
          {
            key: "doc_panTrust",
            flagName: "isPANOfTrustDef",
            isDef: trust.isPANOfTrustDef,
            filePath: trust.trustPanPath || "",
          },
          {
            key: "doc_panTrustee",
            flagName: "isPANOfTrusteeDef",
            isDef: trust.isPANOfTrusteeDef,
            filePath: trust.trusteePanPath || "",
          },
          {
            key: "doc_aadharTrustee",
            flagName: "isAadharOfTrusteeDef",
            isDef: trust.isAadharOfTrusteeDef,
            filePath: trust.trusteeAadharPath || "",
          },
          {
            key: "doc_msme_certificate",
            flagName: "isMSMECertificateDef",
            isDef: msme.isMSMECertificateDef,
            filePath: msme.msmeCertificatePath || "",
          },
          {
            key: "doc_utility_bill",
            flagName: "isUtilityBillDocumentDef",
            isDef: utility.isUtilityBillDocumentDef,
            filePath: utility.utilityBillDocumentPath || "",
          },
          {
            key: "doc_bank_proof",
            flagName: "isPassbookOrCancelledChequeDef",
            isDef: bank.isPassbookOrCancelledChequeDef,
            filePath: bank.passbookOrCancelledChequePath || "",
          },
          {
            key: "third_party_file",
            flagName: "isSignedFormDef",
            isDef: bank.isSignedFormDef,
            filePath: bank.uploadSignedFormPath || "",
          },
        ];
        for (const e of DEF_ENTRIES) {
          if (e.isDef === true)
            map[e.key] = { flagName: e.flagName, filePath: e.filePath };
        }
        return map;
      })(),
    },
    documents,
  };

  return mapped;
};

// ── Action ─────────────────────────────────────────────────────────────────────
export const fetchEntityListAction = async (
  pageNumber = 1,
  pageSize = 10,
  filters = {},
) => {
  try {
    const {
      entityName,
      emailAddress,
      entityCategory,
      status,
      isVerified,
      lobType,
      dateFrom,
      dateTo,
      isRegisteredUnderMSME,
      requireKYC,
      aaDepartmentFunction,
    } = filters;

    const searchValue = entityName || emailAddress || null;
    const loggedInUser = getUserData();
    const body = {
      id: null,
      userEmail: loggedInUser?.email || "", // loggedin user email
      category: entityCategory || null,
      searchValue,
      requireKYC:
        requireKYC === true ? true : requireKYC === false ? false : null,
      documentStatus:
        status === "Done" ? true : status === "pending" ? false : null,
      isVerified:
        isVerified === true ? true : isVerified === false ? false : null,
      // API typo: "Registerd"
      isRegisterdUnderMSME:
        isRegisteredUnderMSME === true
          ? true
          : isRegisteredUnderMSME === false
            ? false
            : null,
      entityLOBType: lobType || null,
      departments: aaDepartmentFunction ? [aaDepartmentFunction] : [],
      startDate: dateFrom ? new Date(dateFrom).toISOString() : null,
      endDate: dateTo
        ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).toISOString()
        : null,
      pageNumber,
      pageSize,
    };

    const response = await apiClient.post(
      ATP_API_URL.GET_ALL_DATA_SEARCH,
      body,
    );
    // console.log("[EntityList] raw response:", response?.data);

    if (response?.status === 200 && response.data) {
      const rawList = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      // API returns all matching records — use totalRecords if provided, else rawList.length
      const totalRecords =
        response.data?.totalRecords ??
        response.data?.pagination?.totalItems ??
        rawList.length;

      const apiTotalPages = response.data?.totalPages;
      const totalPages = apiTotalPages
        ? Math.max(1, apiTotalPages)
        : Math.max(1, Math.ceil(totalRecords / pageSize));

      // Client-side slice if API doesn't paginate server-side
      const isServerPaginated =
        response.data?.totalRecords != null ||
        response.data?.pagination != null;
      const pageData = isServerPaginated
        ? rawList.map(mapRecord)
        : rawList
            .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
            .map(mapRecord);

      return {
        success: true,
        data: pageData,
        pagination: {
          currentPage: pageNumber,
          totalPages,
          totalItems: totalRecords,
          pageSize,
          hasNextPage: pageNumber < totalPages,
          hasPreviousPage: pageNumber > 1,
        },
      };
    }

    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      message: "Failed to fetch entity list",
    };
  } catch (error) {
    console.error("[EntityList] error:", error.message);
    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      message: error.message,
      error,
    };
  }
};
